<template>
	<LayoutBreadcrumbs class="main-wrapper" />

	<div
		v-if="selectedProduct && Object.keys(selectedProduct).length"
		class="flex flex-col mt-6 main-wrapper px-5 w-full mx-auto"
	>
		<div class="flex justify-between gap-12 flex-col lg:flex-row w-full">
			<div v-if="selectedProduct?.Files" class="lg:w-[664px]">
				<StoryblokComponent
					v-for="blok in imageGallery"
					:key="blok._uid"
					:blok="blok"
					:images="
						selectedProduct.Files.length > 0
							? selectedProduct.Files
							: [defaultImage]
					"
				/>
			</div>
			<LazyLoadWrapper>
				<StoryblokComponent
					v-for="blok in productAttributes"
					:key="blok._uid"
					:blok="blok"
					:parametrics="parametrics"
				/>
			</LazyLoadWrapper>
		</div>
		<LazyLoadWrapper>
			<StoryblokComponent
				v-for="blok in productDetails"
				:key="blok._uid"
				:blok="blok"
			/>
			<SimilarProducts v-if="selectedProduct" />
			<ProductPackageProducts
				v-if="selectedProduct"
				:product="selectedProduct"
			/>
			<StoryblokComponent
				v-for="blok in productRecommendations"
				:key="blok._uid"
				:blok="blok"
			/>
		</LazyLoadWrapper>
	</div>
</template>

<script setup lang="ts">
import type {
	GroupedParametrics,
	ParametricListValue
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { storeToRefs } from "pinia";
import LazyLoadWrapper from "~/components/LazyLoadWrapper.vue";
import SimilarProducts from "./SimilarProducts.vue";
import type { BlokGenericForComponents } from "~/types/storyblok/storyblok";

interface Props {
	parametrics: GroupedParametrics | undefined;
	imageGallery: BlokGenericForComponents[] | undefined;
	productAttributes: BlokGenericForComponents[] | undefined;
	productDetails: BlokGenericForComponents[] | undefined;
	colorFromParametricsInLink: ParametricListValue | undefined;
	productRecommendations: BlokGenericForComponents[] | undefined;
}

const {
	parametrics,
	imageGallery,
	productAttributes,
	productDetails,
	colorFromParametricsInLink
} = defineProps<Props>();
const {
	getFirstSecondParametricWithStock,
	selectProductVariant,
	getSecondParametricName,
	helloRetailApi
} = useNorcePwa();

const { selectedProduct, products, selectedColor, selectedSecondParametric } =
	storeToRefs(useProductStore());
const {
	public: {
		norce: { isHelloRetailTracking }
	}
} = useRuntimeConfig();

const productStore = useProductStore();
const defaultImage = computed(() => {
	return {
		id: "default-id",
		Key: "default-key",
		Name: selectedProduct?.value?.Name,
		Path: productStore.defaultProductImage // This will always have the latest value
	};
});

watch(selectedSecondParametric, () => {
	if (parametrics) {
		useProductStore().addSelectedProduct(
			selectProductVariant({
				selectedColorId: selectedColor.value?.Id,
				selectedSecondParametricId: selectedSecondParametric.value?.Id,
				products: products.value!,
				parametricsAvailable:
					parametrics?.color ??
					parametrics?.[getSecondParametricName(parametrics)]
			})
		);
	}
});

watch(selectedColor, () => {
	if (
		parametrics &&
		parametrics[getSecondParametricName(parametrics)] &&
		!colorFromParametricsInLink &&
		selectedColor.value
	) {
		const selectedSecondParametricLocal = getFirstSecondParametricWithStock({
			parametrics: parametrics[getSecondParametricName(parametrics)],
			products: products.value!,
			selectedColor: selectedColor.value
		});
		if (selectedSecondParametricLocal) {
			useProductStore().addSelectedSecondParametric(
				selectedSecondParametricLocal
			);
		}
	}
});

onMounted(() => {
	if (!isHelloRetailTracking) return;

	helloRetailApi.track({
		serviceType: "pageview",
		trackingData: {
			location: window.location.href,
			productNumber: selectedProduct.value?.PartNo || "",
			referrer: window.location.origin
		}
	});
});
</script>
